import { useEffect, useRef, useState } from 'react';
import { useEntity } from '@backstage/plugin-catalog-react';
import { useApi, fetchApiRef, discoveryApiRef } from '@backstage/core-plugin-api';
import { MarkdownContent, Progress, ErrorPanel } from '@backstage/core-components';
import { makeStyles } from 'tss-react/mui';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

const SOURCE_LOCATION_ANNOTATION = 'backstage.io/source-location';

interface TocItem {
  id: string;
  text: string;
  level: number;
}

// ---------------------------------------------------------------------------
// Styles
// All typography values are explicit (no theme.typography spread) to avoid
// tss-react/mui type inference issues with the MUI Fontface interface.
// Scale mirrors MkDocs Material theme for visual consistency with TechDocs.
// ---------------------------------------------------------------------------
const useStyles = makeStyles()((theme) => ({
  layout: {
    display: 'flex',
    padding: theme.spacing(3, 4),
    gap: theme.spacing(4),
    alignItems: 'flex-start',
  },
  contentArea: {
    flex: 1,
    minWidth: 0,
  },
  // ---------------------------------------------------------------------------
  // Font sizes are computed with the same formula TechDocs uses internally:
  //   px / theme.typography.htmlFontSize * 0.6  →  rem
  // Backstage defaultTypography: h1=54, h2=40, h3=32, h4=28, h5=24, h6=20 (px)
  // htmlFontSize=16  →  2.025 / 1.5 / 1.2 / 1.05 / 0.9 / 0.75 rem
  // Base font is Roboto (MkDocs Material explicit), 1rem body (TechDocs overrides
  // MkDocs' 0.8rem base via --md-typeset-font-size: 1rem).
  // ---------------------------------------------------------------------------
  prose: {
    fontFamily: 'Roboto, -apple-system, BlinkMacSystemFont, Helvetica, Arial, sans-serif',
    fontSize: '1rem',
    lineHeight: 1.6,
    '& h1': {
      fontSize: '2.025rem',   // 54 / 16 * 0.6
      fontWeight: 700,
      lineHeight: 1.3,
      marginTop: 0,
      marginBottom: theme.spacing(2),
      paddingBottom: theme.spacing(1),
      borderBottom: `1px solid ${theme.palette.divider}`,
    },
    '& h2': {
      fontSize: '1.5rem',     // 40 / 16 * 0.6
      fontWeight: 700,
      lineHeight: 1.4,
      marginTop: theme.spacing(5),
      marginBottom: theme.spacing(1.5),
      paddingBottom: theme.spacing(0.5),
      borderBottom: `1px solid ${theme.palette.divider}`,
    },
    '& h3': {
      fontSize: '1.2rem',     // 32 / 16 * 0.6
      fontWeight: 700,
      lineHeight: 1.5,
      marginTop: theme.spacing(4),
      marginBottom: theme.spacing(1),
    },
    '& h4': {
      fontSize: '1.05rem',    // 28 / 16 * 0.6
      fontWeight: 700,
      lineHeight: 1.5,
      marginTop: theme.spacing(3),
      marginBottom: theme.spacing(0.75),
    },
    '& h5': {
      fontSize: '0.9rem',     // 24 / 16 * 0.6
      fontWeight: 700,
      lineHeight: 1.5,
      marginTop: theme.spacing(2),
      marginBottom: theme.spacing(0.5),
    },
    '& h6': {
      fontSize: '0.75rem',    // 20 / 16 * 0.6
      fontWeight: 700,
      lineHeight: 1.5,
      marginTop: theme.spacing(2),
      marginBottom: theme.spacing(0.5),
    },
    '& p': {
      fontSize: '1rem',
      lineHeight: 1.6,
      marginTop: 0,
      marginBottom: theme.spacing(2),
    },
    '& a': {
      color: theme.palette.primary.main,
      textDecoration: 'none',
      '&:hover': { textDecoration: 'underline' },
    },
    '& pre': {
      backgroundColor: theme.palette.mode === 'dark'
        ? theme.palette.grey[900]
        : theme.palette.grey[100],
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: theme.shape.borderRadius,
      padding: theme.spacing(2),
      overflowX: 'auto',
      marginBottom: theme.spacing(2),
      lineHeight: 1.6,
      '& code': {
        background: 'transparent',
        padding: 0,
        fontSize: '0.875em',
        fontFamily: '"Roboto Mono", "SFMono-Regular", Consolas, monospace',
      },
    },
    '& :not(pre) > code': {
      backgroundColor: theme.palette.mode === 'dark'
        ? theme.palette.grey[800]
        : theme.palette.grey[200],
      borderRadius: 3,
      padding: '2px 6px',
      fontSize: '0.85em',
      fontFamily: '"Roboto Mono", "SFMono-Regular", Consolas, monospace',
    },
    '& blockquote': {
      borderLeft: `0.2rem solid ${theme.palette.mode === 'dark' ? theme.palette.grey[600] : theme.palette.grey[400]}`,
      margin: theme.spacing(2, 0),
      padding: theme.spacing(0.5, 2),
      color: theme.palette.text.secondary,
      '& > p': { marginBottom: 0, color: theme.palette.text.secondary },
    },
    '& table': {
      width: '100%',
      borderCollapse: 'collapse',
      marginBottom: theme.spacing(2),
      display: 'block',
      overflowX: 'auto',
      fontSize: '1rem',
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: theme.shape.borderRadius,
      borderBottom: 'none',
    },
    '& th': {
      fontWeight: 700,
      backgroundColor: theme.palette.mode === 'dark'
        ? theme.palette.grey[800]
        : theme.palette.grey[100],
      padding: theme.spacing(1, 2),
      textAlign: 'left',
      borderBottom: `1px solid ${theme.palette.divider}`,
      whiteSpace: 'nowrap',
    },
    '& td': {
      padding: theme.spacing(1, 2),
      borderBottom: `1px solid ${theme.palette.divider}`,
      verticalAlign: 'top',
      lineHeight: 1.6,
    },
    '& tr:last-child td': { borderBottom: '1px solid transparent' },
    // GFM task list checkboxes
    '& .task-list-item': {
      listStyle: 'none',
      marginLeft: theme.spacing(-3),
      paddingLeft: theme.spacing(3),
    },
    '& .task-list-item input[type="checkbox"]': {
      marginRight: theme.spacing(0.75),
      verticalAlign: 'middle',
    },
    // Inline text formatting
    '& del': { opacity: 0.5 },
    '& mark': {
      backgroundColor: theme.palette.mode === 'dark'
        ? 'rgba(255, 213, 0, 0.25)'
        : 'rgba(255, 213, 0, 0.5)',
      borderRadius: 2,
      padding: '0 2px',
    },
    // Keyboard key styling — common in READMEs
    '& kbd': {
      display: 'inline-block',
      padding: '0.1em 0.4em',
      fontSize: '0.85em',
      fontFamily: '"Roboto Mono", "SFMono-Regular", Consolas, monospace',
      backgroundColor: theme.palette.mode === 'dark'
        ? theme.palette.grey[800]
        : theme.palette.grey[100],
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: 4,
      boxShadow: `0 1px 0 ${theme.palette.divider}`,
      verticalAlign: 'middle',
    },
    '& ul, & ol': {
      paddingLeft: theme.spacing(3),
      marginTop: 0,
      marginBottom: theme.spacing(2),
      fontSize: '1rem',
      lineHeight: 1.6,
    },
    '& li': { marginBottom: theme.spacing(0.5) },
    '& li > ul, & li > ol': { marginTop: theme.spacing(0.5), marginBottom: 0 },
    '& img': { maxWidth: '100%', height: 'auto', borderRadius: theme.shape.borderRadius },
    '& hr': {
      border: 'none',
      borderTop: `1px solid ${theme.palette.divider}`,
      margin: theme.spacing(3, 0),
    },
    '& details': {
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: theme.shape.borderRadius,
      padding: theme.spacing(1, 2),
      marginBottom: theme.spacing(2),
    },
    '& summary': {
      cursor: 'pointer',
      fontWeight: 600,
      padding: theme.spacing(0.5, 0),
    },
  },
  // ---------------------------------------------------------------------------
  // Table of contents — mirrors TechDocs "On this page" sidebar
  // ---------------------------------------------------------------------------
  tocSidebar: {
    width: 220,
    flexShrink: 0,
    position: 'sticky',
    top: theme.spacing(2),
    alignSelf: 'flex-start',
    maxHeight: 'calc(100vh - 180px)',
    overflowY: 'auto',
    paddingLeft: theme.spacing(2),
    borderLeft: `1px solid ${theme.palette.divider}`,
  },
  tocTitle: {
    display: 'block',
    fontSize: '0.7rem',
    fontWeight: 700,
    letterSpacing: '0.08em',
    textTransform: 'uppercase' as const,
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(1),
  },
  tocList: {
    listStyle: 'none',
    padding: 0,
    margin: 0,
  },
  tocItem: {
    display: 'block',
    padding: `${theme.spacing(0.35)} 0`,
    fontSize: '0.8rem',
    lineHeight: 1.5,
    color: theme.palette.text.secondary,
    textDecoration: 'none',
    cursor: 'pointer',
    transition: 'color 0.12s',
    '&:hover': { color: theme.palette.text.primary },
  },
  tocItemH3: {
    paddingLeft: theme.spacing(1.5),
    fontSize: '0.75rem',
  },
  tocItemActive: {
    color: theme.palette.primary.main,
    fontWeight: 600,
  },
}));

// GitHub-compatible slug with duplicate handling
function slugify(text: string, seen: Map<string, number>): string {
  const base = text
    .toLowerCase()
    .replace(/[^\w\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-');
  const n = seen.get(base) ?? 0;
  seen.set(base, n + 1);
  return n === 0 ? base : `${base}-${n}`;
}

function extractBlobUrl(sourceLocation: string): string {
  return sourceLocation.startsWith('url:') ? sourceLocation.slice(4) : sourceLocation;
}

function isGitHubUrl(url: string): boolean {
  try { return new URL(url).hostname === 'github.com'; } catch { return false; }
}

export function AgenticDocsContent() {
  const { entity } = useEntity();
  const fetchApi = useApi(fetchApiRef);
  const discoveryApi = useApi(discoveryApiRef);
  const { classes, cx } = useStyles();

  const sourceLocation = entity.metadata.annotations?.[SOURCE_LOCATION_ANNOTATION];
  const blobUrl = sourceLocation ? extractBlobUrl(sourceLocation) : undefined;
  const validUrl = !!(blobUrl && isGitHubUrl(blobUrl));

  const [markdown, setMarkdown] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [tocItems, setTocItems] = useState<TocItem[]>([]);
  const [activeId, setActiveId] = useState('');
  const contentRef = useRef<HTMLDivElement>(null);

  // Fetch raw markdown via the backend urlReader proxy
  useEffect(() => {
    if (!blobUrl || !validUrl) return undefined;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setMarkdown(null);
    setTocItems([]);

    (async () => {
      try {
        const base = await discoveryApi.getBaseUrl('oneadvanced');
        const res = await fetchApi.fetch(
          `${base}/agentic-catalog/raw-content?url=${encodeURIComponent(blobUrl)}`,
        );
        if (!res.ok) throw new Error(`Failed to load README (${res.status})`);
        const text = await res.text();
        if (!cancelled) setMarkdown(text);
      } catch (err) {
        if (!cancelled) setError(err as Error);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [blobUrl, validUrl, fetchApi, discoveryApi]);

  // After markdown renders: scan headings, assign IDs, build ToC list
  useEffect(() => {
    if (!contentRef.current || !markdown) return;
    const seen = new Map<string, number>();
    const items: TocItem[] = [];
    contentRef.current.querySelectorAll<HTMLElement>('h1, h2, h3').forEach(el => {
      const text = el.textContent ?? '';
      const id = slugify(text, seen);
      el.id = id;
      items.push({ id, text, level: parseInt(el.tagName[1], 10) });
    });
    setTocItems(items);
  }, [markdown]);

  // Track the active heading with IntersectionObserver
  useEffect(() => {
    if (!contentRef.current || tocItems.length === 0) return undefined;
    const observer = new IntersectionObserver(
      entries => {
        const hit = entries.find(e => e.isIntersecting);
        if (hit) setActiveId(hit.target.id);
      },
      { rootMargin: '-10% 0% -80% 0%', threshold: 0 },
    );
    contentRef.current.querySelectorAll('h1, h2, h3').forEach(h => observer.observe(h));
    return () => observer.disconnect();
  }, [tocItems]);

  const scrollTo = (e: React.MouseEvent, id: string) => {
    e.preventDefault();
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setActiveId(id);
  };

  if (!validUrl) {
    return (
      <Box p={3}>
        <Typography variant="body1" color="textSecondary">
          No <code>{SOURCE_LOCATION_ANNOTATION}</code> annotation found on this entity. Add it
          pointing to the README on GitHub to display inline docs.
        </Typography>
      </Box>
    );
  }

  if (loading) return <Progress />;

  if (error) {
    return (
      <Box p={3}>
        <ErrorPanel error={error} />
      </Box>
    );
  }

  return (
    <div className={classes.layout}>
      {/* Main content */}
      <div className={classes.contentArea}>
        {markdown && (
          <div ref={contentRef} className={classes.prose}>
            <MarkdownContent content={markdown} dialect="gfm" />
          </div>
        )}
      </div>

      {/* Table of contents */}
      {tocItems.length > 0 && (
        <nav className={classes.tocSidebar}>
          <span className={classes.tocTitle}>On this page</span>
          <ul className={classes.tocList}>
            {tocItems.map(item => (
              <li key={`${item.id}-${item.level}`}>
                <a
                  href={`#${item.id}`}
                  className={cx(
                    classes.tocItem,
                    item.level === 3 && classes.tocItemH3,
                    activeId === item.id && classes.tocItemActive,
                  )}
                  onClick={e => scrollTo(e, item.id)}
                >
                  {item.text}
                </a>
              </li>
            ))}
          </ul>
        </nav>
      )}
    </div>
  );
}
